/**
 * The value in force of every registry key a run read — bata-reqs ADR 9001 decision 5 and ADR 9000,
 * built by bata-platform ADR 9045 (the run records it; platform #890).
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * The client is re-pinned to the bata-platform contract that publishes both halves (released with
 * v0.125.0):
 *
 *   GET /api/v1/exit/consolidation/runs/{run_id}/parameters     a new read (`ParametersInForceSummary`)
 *   GET /api/v1/inseason/runs/{run_id}/provenance               gains `parameters_in_force` (same shape)
 *
 * So the shapes below are the generated `ParametersInForceSummary` / `ParameterInForce`, re-exported
 * under the names the sheet builder already imports; {@link CONSOLIDATION_RUN_PARAMETERS_READ} is the
 * generated `getConsolidationRunParameters`, and {@link PROVENANCE_CARRIES_PARAMETERS} is `true`. Until
 * this re-pin the shapes were typed here by hand and the read was `null`; the generated models matched
 * them field for field.
 *
 * A record the platform does not compose (`parameters_in_force: null`) — or a serving process still on
 * an older release, whose provenance has no such field — is read as `not_published`. Either way the
 * «Parametri in vigore» sheet says plainly that the file cannot state its run's parameters — it NEVER
 * fills them from today's registry (`GET /registry/parameters`), which is exactly what ADR 9001 forbids.
 *
 * `run-parameters.test.ts` keeps the tripwire: the pinned spec has the path exactly when the constant is
 * wired, and carries the provenance field exactly when {@link PROVENANCE_CARRIES_PARAMETERS} says so.
 * **Every field access of the platform's shapes is in this file.**
 */

import type { ParameterInForce, ParametersInForceSummary, RunProvenanceSummary } from '../generated';
import {
  getConsolidationRunParameters, type GetConsolidationRunParametersParams, type OperationSpec,
} from '../generated/operations';

/** One registry row in force for a key the run read, or the key's absence (`absent_reason: not_in_force`). */
export type ParameterInForceShape = ParameterInForce;

/** What one run recorded of the registry. `recorded: false` → `absent_reason: not_recorded_by_run`. */
export type ParametersInForceShape = ParametersInForceSummary;

export type ConsolidationRunParametersParams = GetConsolidationRunParametersParams;
export type ConsolidationRunParametersRead = OperationSpec<ConsolidationRunParametersParams, ParametersInForceShape>;

export const CONSOLIDATION_RUN_PARAMETERS_PATH_IN_SPEC = '/api/v1/exit/consolidation/runs/{run_id}/parameters';
/** The operation as the platform declares it — now the generated one. */
export const CONSOLIDATION_RUN_PARAMETERS_PROPOSED_OPERATION: ConsolidationRunParametersRead = getConsolidationRunParameters;
/** The pinned read. `null` would issue no request and state `not_published`. */
export const CONSOLIDATION_RUN_PARAMETERS_READ: ConsolidationRunParametersRead | null = getConsolidationRunParameters;

/** Whether the pinned `RunProvenanceSummary` declares `parameters_in_force`. */
export const PROVENANCE_CARRIES_PARAMETERS = true;

/**
 * The in-season run's record, from its provenance read. `undefined` when the serving platform does not
 * publish the field (a release before bata-platform ADR 9045); `null` when it publishes no source for it.
 */
export function provenanceParametersOf(summary: RunProvenanceSummary): ParametersInForceShape | null | undefined {
  return summary.parameters_in_force;
}
